//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';
import InfoRoundedIcon from '@material-ui/icons/InfoOutlined';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { setClassNameInDefns } from '../../common/definitionHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
// import { PLACEHOLDER_FIELD_COUNTRY, getStateLovPlaceholder, getCityLovPlaceholder, PLACEHOLDER_FIELD_STATE, PLACEHOLDER_FIELD_CITY } from '../../common/placeholderHelper';
// import { getStateLabelByCountryName } from '../../common/locationHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import {
    EDIT_INFO,
    EDIT_MEMBER_INFO,
    EDIT_OWNER_ADMIN_INFO,
    SET_UP_MEMBER_INFO,
    TOOLTIP_EMP_MEMBER_COUNTRY,
    TOOLTIP_EMP_MEMBER_USE_COMPANY_LOCATION,
    USE_COMPANY_LOCATION,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { deleteEmpMemberProfilePic, saveEmpMember, uploadEmpMemberProfilePic } from '../../common/services/empMySettingsServices';
// import { detectInappropriatePicture } from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdProfilePicCropper from '../../common/elemental/AfitdProfilePicCropper';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';
// import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdAvatarWithRing from '../../common/elemental/AfitdAvatarWithRing';
// import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import LoadingGif from '../../assets/loading.gif';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
// import _find from 'lodash/find';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        avatarActive: { width: theme.spacing(12), height: theme.spacing(12), borderRadius: '100%' },
        avatar: { photoSize: 90, borderSize: 4, placeholderFontSize: 30 },
        avatarBox: {
            width: theme.spacing(12),
            height: theme.spacing(12),
            background: '#FFFFFF',
            border: '1px dashed #D5D7DD',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        avatarBoxActive: {
            width: theme.spacing(12),
            height: theme.spacing(12),
            borderRadius: '100%',
            padding: 3,
            border: '1px solid red',
            // background: 'linear-gradient(to right, #FC821F, #1D8A8B)',
            // // As fallback for browsers which do not support gradient
            // fallbacks: [
            //     { background: '#f6b73c' },
            //     { background: '-o-linear-gradient(360deg, #f6b73c, #1D8A8B)' },
            //     { background: '-moz-linear-gradient(360deg, #f6b73c, #1D8A8B)' },
            //     { background: 'linear-gradient(360deg, #f6b73c, #1D8A8B)' },
            // ],
        },
        btnBox: { padding: 10, paddingTop: 30 },
        instructionText: { marginTop: 5, color: 'rgba(17, 30, 20, 0.4)' },
        useCompanyLocationLinkContainer: { display: 'flex', margin: '20px 0 0 5px' },
        useCompanyLocationLinkSpan: { marginRight: 5 },
        useCompanyLocationLink: { textDecoration: 'underline', color: 'blue' },
        inputBoxContainer: { display: 'flex', margin: '10px 0' },
        inputBox: { flex: 1, padding: '0 5px' },
        label: { padding: '5px 0', fontWeight: 'bold' },
        dropDown: { fontSize: '16px', color: '#1D8A8B', border: '1px solid #D5D7DD', borderRadius: 4 },
        focusDropDown: { fontSize: '16px', color: '#1D8A8B', border: '2px solid #1D8A8B', borderRadius: 4 },
        buttonBox: { display: 'flex', justifyContent: 'flex-end', padding: '10px 0', [theme.breakpoints.down('sm')]: { justifyContent: 'space-between' } },
        heading: { fontWeight: 'bold' },

        rightMarginToLeftButton: { marginRight: 16 },
        generalAvatarContainer: { display: 'flex', margin: '30px 0', alignItems: 'center' },
        hidden: { display: 'none' },
        cropContainer: { height: 250, width: '100%', textAlign: 'center' },
        imgBeforeCropContainer: { display: 'flex', justifyContent: 'center' },
        uploadLinearProgressContainer: { marginTop: 30, padding: '0 20px' },
        countryError: { color: 'red' },
        stateError: { color: 'red' },
        cityError: { color: 'red' },
        infoIcon: {
            color: 'black',
            background: 'white',
            fontSize: '15px',
            cursor: 'pointer',
        },
        toolTipTitle: {
            backgroundColor: 'white',
            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
            borderRadius: '11px',
            fontSize: '14px',
            padding: '8px',
            color: 'black',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        appContext,
        props,
        handleChangeFields,
        handleSaveButton,
        data,
        deletePhotoModal,
        setDeletePhotoModal,
        cropModalOpen,
        setCropModalOpen,
        handleChangePicUploadInput,
        handleUploadPic,
        handleDeletePic,
        progress,
        tempImage,
        setBlob,
        cancelModal,
        setCancelModal,
        prevData,
        handleCancelButton,
        saveLoading,
        country,
        // setCountry,
        state,
        // setState,
        city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        // countryLov,
        // stateLov,
        // cityLov,
        countryError,
        // setCountryError,
        stateError,
        // setStateError,
        cityError,
        // setCityError,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        // handleChangeCountry,
        // handleChangeState,
        // handleChangeCity,
        handleClickUseCompanyLocationLink,
    } = params;
    const { openEditModal, variant, isPictureinProcess } = props;

    const {
        rightMarginToLeftButton: rightMarginToLeftButtonCls,
        avatarBoxActive: avatarBoxActiveCls,
        avatarActive: avatarActiveCls,
        avatarBox: avatarBoxCls,
        avatar: avatarCls,
        hidden: hiddenCls,
    } = classes;
    const { profilePicUrl, firstName, lastName, middleName } = data;

    let dialogTitle = EDIT_INFO;

    switch (variant) {
        case VARIANT_OWNER:
            dialogTitle = EDIT_OWNER_ADMIN_INFO;
            break;
        case VARIANT_NEW_MEMBER:
        case VARIANT_EXISTING_MEMBER:
            dialogTitle = EDIT_MEMBER_INFO;
            break;
        case VARIANT_MEMBER_SETUP: {
            const companyName = appContext.currentEmployer?.companyName || '';
            dialogTitle = (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div>{SET_UP_MEMBER_INFO}</div>
                    <AfitdTypography component="div">{companyName}</AfitdTypography>
                </div>
            );
            break;
        }
        default:
        // do nothing
    }

    // const setupMode = variant === VARIANT_MEMBER_SETUP;

    // figure out if the page is dirty. if setup mode, it is always dirty.
    const allFields = { ...data, country, state, city };
    const isDirty = !Object.keys(allFields).every(key => _isEqual(allFields[key], prevData.current[key]));

    const isProfilePicture = profilePicUrl || false;
    // const stateDisabled = !country;
    // const cityDisabled = !state || !country;
    // const stateLabelValue = getStateLabelByCountryName(country);
    // const statePlaceholder = stateDisabled ? '' : getStateLovPlaceholder(stateLov, PLACEHOLDER_FIELD_STATE);
    // const cityPlaceholder = cityDisabled ? '' : getCityLovPlaceholder(cityLov, PLACEHOLDER_FIELD_CITY);

    const defns = {
        container: {
            open: openEditModal,
            onClose: () => (isDirty ? setCancelModal(true) : handleCancelButton()),
            // isCloseable: variant !== VARIANT_MEMBER_SETUP,
            label: dialogTitle,
            actionButtonDefns: {
                save: {
                    disabled: !isDirty,
                    loading: saveLoading,
                    onClick: handleSaveButton,
                    label: 'Save Changes',
                },
            },
        },
        avatarBox: { className: cx(isProfilePicture ? avatarBoxActiveCls : avatarBoxCls) },
        avatar: {
            src: isPictureinProcess ? LoadingGif : profilePicUrl,
            className: cx(isProfilePicture ? avatarActiveCls : avatarCls),
            photoSize: 85,
            borderSize: 4,
            placeholderFontSize: 30,
        },
        generalUploadPicButton: {
            className: rightMarginToLeftButtonCls,
            color: 'secondary',
            title: isProfilePicture ? 'Upload new photo' : 'Upload photo',
            component: 'label',
            disabled: isPictureinProcess,
        },
        generalDeletePicButton: { className: cx({ [hiddenCls]: !isProfilePicture }), onClick: () => setDeletePhotoModal(!deletePhotoModal), disabled: isPictureinProcess },
        firstName: { onChange: handleChangeFields, name: 'firstName', value: firstName, placeholder: 'First Name' },
        lastName: { onChange: handleChangeFields, name: 'lastName', value: lastName, placeholder: 'Last Name' },
        middleName: { onChange: handleChangeFields, name: 'middleName', value: middleName, placeholder: 'Middle Name' },

        label: { variant: 'body2' },
        picUploadInput: { type: 'file', hidden: true, onChange: handleChangePicUploadInput, accept: '.jpg, .png' },
        cropModal: {
            open: cropModalOpen,
            onClose: () => setCropModalOpen(false),
            label: 'Profile Picture',
            actionButtonDefns: {
                cancel: { disabled: false, onClick: () => setCropModalOpen(false) },
                save: {
                    disabled: false,
                    loading: saveLoading,
                    onClick: handleUploadPic,
                    label: 'Save Changes',
                },
            },
        },
        cropper: { file: tempImage, setBlob: setBlob },
        uploadLinearProgress: { variant: 'determinate', value: progress, isHidden: _isNil(progress) },
        confirmDeletePhotoModal: {
            title: 'Delete Photo',
            onClose: () => setDeletePhotoModal(false),
            onConfirm: handleDeletePic,
            open: deletePhotoModal,
            message: 'Are you sure you want to delete your photo?',
        },

        cancelModal: {
            title: 'Cancel?',
            onClose: () => setCancelModal(false),
            onConfirm: handleCancelButton,
            open: cancelModal,
            message: 'All unsaved changes will be lost.',
        },
        // badImageDialog: {
        //     title: 'Inappropriate Images are not Allowed!',
        //     children: (
        //         <AfitdTypography>
        //             We detected <b>{badImageDialogMessage.join(',')}</b> in your picture which does not meet our community guidelines. Inappropriate Images cannot be saved to this
        //             platform.
        //         </AfitdTypography>
        //     ),
        //     open: openBadImageDialog,
        //     onClose: () => {
        //         setOpenBadImageDialog();
        //         handleCancelButton();
        //         setCropModalOpen(false);
        //         // closeEditModal();
        //         onClose && onClose();
        //     },
        // },
        generalCountry: {
            // onChange: handleChangeCountry,
            // value: country,
            // options: countryLov,
            // id: 'country',
            // placeholder: PLACEHOLDER_FIELD_COUNTRY,
            ...countryProps,
            error: countryError,
        },
        generalStateLabel: {
            // value: stateLabelValue,
            ...stateLabelProps,
        },
        generalState: {
            // // disabled: !country,
            // onChange: handleChangeState,
            // value: state,
            // id: 'state',
            // options: stateLov,
            // placeholder: statePlaceholder,
            ...stateProps,
            error: stateError,
        },
        generalCity: {
            // // disabled: !state,
            // onChange: handleChangeCity,
            // value: city,
            // id: 'city',
            // options: cityLov,
            // placeholder: cityPlaceholder,
            ...cityProps,
            error: cityError,
        },
        countryError: { hidden: !countryError },
        stateError: { hidden: !stateError },
        cityError: { hidden: !cityError },
        countryToolTip: {
            title: "If your work location is different from your company's location, please change it here.",
        },
        useCompanyLocationLink: { onClick: handleClickUseCompanyLocationLink },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
export const VARIANT_OWNER = 1;
export const VARIANT_NEW_MEMBER = 2;
export const VARIANT_EXISTING_MEMBER = 3;
export const VARIANT_MEMBER_SETUP = 4;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function EditProfileDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { employerId, currentEmployer, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    // const employerCountry = _get(currentEmployer, 'country');
    // const employerState = _get(currentEmployer, 'state');
    // const employerCity = _get(currentEmployer, 'city');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        onCancel,
        onDidSave,
        onProfilePicDidDelete,
        onProfilePicDidSave,
        memberUserId,
        profilePicUrl: profilePicUrlProp,
        firstName: firstNameProp,
        lastName: lastNameProp,
        middleName: middleNameProp,
        country: countryProp,
        state: stateProp,
        city: cityProp,
        cityLocation: cityLocationProp,
    } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [deletePhotoModal, setDeletePhotoModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [blob, setBlob] = useState(null);
    const [tempImage, setTempImage] = useState('');
    const [progress, setProgress] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [countryError, setCountryError] = useState(false);
    const [stateError, setStateError] = useState(false);
    const [cityError, setCityError] = useState(false);

    const {
        // countryLov,
        // stateLov,
        // setStateLov,
        // cityLov,
        // setCityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
    });

    const [data, setData] = useState({ firstName: '', lastName: '', middleName: '', country: null, state: null, city: null });
    // const [data, setData] = useState({ firstName: '', lastName: '', middleName: '', country: '', state: '', city: '' });
    let prevData = useRef({ ...data });

    // const { profilePicUrl, city: cityProp, state: stateProp, country: countryProp } = dataFromProps;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const dataFromProps = {
            profilePicUrl: profilePicUrlProp,
            firstName: firstNameProp,
            lastName: lastNameProp,
            middleName: middleNameProp,
            country: countryProp || null,
            state: stateProp || null,
            city: cityProp || null,
            cityLocation: cityLocationProp || null,
        };
        prevData.current = { ...dataFromProps };
        setData(dataFromProps);

        setCountry(countryProp || null);
        setState(stateProp || null);
        setCity(cityProp || null);
        setCityLocation(cityLocationProp || null);
    }, [profilePicUrlProp, firstNameProp, lastNameProp, middleNameProp, countryProp, stateProp, cityProp, cityLocationProp, setCountry, setState, setCity, setCityLocation]);

    // useEffect(() => {
    //     if (countryProp) {
    //         setCountry(countryProp);
    //         setState(stateProp);
    //         setCity(cityProp);
    //     } else {
    //         setCountry(employerCountry);
    //         setState(employerState);
    //         setCity(employerCity);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [employerCountry]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isUseCompanyLocationLinkShow = !!!country; // show link only if country is nil

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData({ ...data, [name]: value });
    };

    const handleCancelButton = () => {
        // setData({ ...dataFromProps });
        onCancel && onCancel();
    };

    const handleSaveButton = async () => {
        if (!country) setCountryError(true);
        if (!state) setStateError(true);
        if (!city) setCityError(true);

        if (country && state && city) {
            const isSkipActivateAccount = !!countryProp; // activate account if country from db was nil; otherwise, skip it

            // Call the new service
            setSaveLoading(true);
            const success = await saveEmpMember({
                employerId,
                userId: memberUserId,
                firstName: data?.firstName,
                middleName: data?.middleName,
                lastName: data?.lastName,
                country,
                state,
                city,
                cityLocation,
                newCity, //TODO save new city
                isSkipActivateAccount,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });

            if (success) {
                setCountry(null);
                setState(null);
                setCity(null);
                setCityLocation(null);

                onDidSave && onDidSave();
            }

            setSaveLoading(false);
        }
    };

    const handleChangePicUploadInput = evt => {
        const file = evt.target.files[0];
        if (file?.size < 5000000) {
            setTempImage(file);
            setCropModalOpen(true);
        } else {
            console.log('file is huge');
        }
    };

    const handleUploadPic = async () => {
        setSaveLoading(true);
        await uploadEmpMemberProfilePic({
            file: blob,
            employerId,
            userId: memberUserId,
            // setProfilePicture: url => setData({ ...data, profilePicUrl: url }),
            setSnackbarMessage,
            setSnackbarErrorMessage,
            setProgress,
            callback: () => {
                setCropModalOpen(false);
                // closeEditModal();
                onProfilePicDidSave && onProfilePicDidSave();
                setSaveLoading(false);
            },
        });
    };

    const handleDeletePic = async () => {
        const success = await deleteEmpMemberProfilePic({ prevPicUrl: profilePicUrlProp, employerId, userId: memberUserId, setSnackbarMessage, setSnackbarErrorMessage });
        // closeEditModal();
        success && onProfilePicDidDelete && onProfilePicDidDelete();
    };

    const handleClickUseCompanyLocationLink = () => {
        setCountry(currentEmployer.country);
        setState(currentEmployer.state);
        setCity(currentEmployer.city);
        setCityLocation(currentEmployer.cityLocation);
    };

    // const handleChangeCountry = (evt, option) => {
    //     if (option) setCountryError(false);
    //     setCountry(option?.name);
    //     setData({ ...data, country: option?.name });
    //     setState(null);
    //     setCity(null);
    //     setCityLocation(null);
    // };

    // const handleChangeState = (evt, option) => {
    //     if (option) setStateError(false);
    //     setState(option?.name);
    //     setData({ ...data, state: option?.name });
    //     setCity(null);
    //     setCityLocation(null);
    // };

    // const handleChangeCity = (evt, option) => {
    //     if (option) setCityError(false);
    //     // City is a single-select creatable. Do not extract the name because the option can potentially be:
    //     //   { name, location }
    //     //   { inputValue, name, label }  (service will need inputValue to inserting the new value in db)
    //     setCity(option);
    //     setData({ ...data, city: option, state, country }); // in case user only changes the city , it should display the country as well.
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        appContext,
        props,
        handleChangeFields,
        handleSaveButton,
        data,
        deletePhotoModal,
        setDeletePhotoModal,
        cropModalOpen,
        setCropModalOpen,
        handleChangePicUploadInput,
        handleUploadPic,
        handleDeletePic,
        progress,
        tempImage,
        setBlob,
        cancelModal,
        setCancelModal,
        prevData,
        handleCancelButton,
        saveLoading,
        country,
        // setCountry,
        state,
        // setState,
        city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        // countryLov,
        // stateLov,
        // cityLov,
        countryError,
        // setCountryError,
        stateError,
        // setStateError,
        cityError,
        // setCityError,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        // handleChangeCountry,
        // handleChangeState,
        // handleChangeCity,
        handleClickUseCompanyLocationLink,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            <div {...defns.generalAvatarContainer}>
                <div>
                    <AfitdAvatarWithRing {...defns.avatar} />
                </div>
                <div {...defns.btnBox}>
                    <div>
                        <AfitdButton {...defns.generalUploadPicButton}>
                            {defns.generalUploadPicButton.title}
                            <input {...defns.picUploadInput} />
                        </AfitdButton>
                        <AfitdButton {...defns.generalDeletePicButton}>Delete</AfitdButton>
                    </div>
                    <AfitdTypography {...defns.instructionText}>JPG and PNG files are the accepted file types.</AfitdTypography>
                </div>
            </div>
            <div>
                <div {...defns.inputBox}>
                    <AfitdTypography {...defns.label}>First Name *</AfitdTypography>
                    <AfitdTextField {...defns.firstName} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Middle Name</AfitdTypography>
                        <AfitdTextField {...defns.middleName} />
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Last Name *</AfitdTypography>
                        <AfitdTextField {...defns.lastName} />
                    </div>
                </div>
            </div>
            {isUseCompanyLocationLinkShow && (
                <div {...defns.useCompanyLocationLinkContainer}>
                    <span {...defns.useCompanyLocationLinkSpan}>
                        <AfitdLink {...defns.useCompanyLocationLink}>{USE_COMPANY_LOCATION}?</AfitdLink>
                    </span>
                    <Tooltip classes={{ tooltip: classes.toolTipTitle }} placement="right-end" title={TOOLTIP_EMP_MEMBER_USE_COMPANY_LOCATION}>
                        <InfoRoundedIcon {...defns.infoIcon} />
                    </Tooltip>
                </div>
            )}
            <div {...defns.inputBoxContainer}>
                <div {...defns.inputBox}>
                    <AfitdTypography {...defns.label}>
                        Country *{' '}
                        <Tooltip classes={{ tooltip: classes.toolTipTitle }} placement="right-end" title={TOOLTIP_EMP_MEMBER_COUNTRY}>
                            <InfoRoundedIcon {...defns.infoIcon} />
                        </Tooltip>
                    </AfitdTypography>
                    {/* <AftidAutocomplete {...defns.generalCountry} /> */}
                    <AfitdCountryAutocomplete {...defns.generalCountry} />
                    <span {...defns.countryError}>Country is required</span>
                </div>
                <div {...defns.inputBox}>
                    <AfitdTypography {...defns.label}>{defns.generalStateLabel.value} *</AfitdTypography>
                    {/* <AftidAutocomplete {...defns.generalProvince} /> */}
                    <AfitdStateAutocomplete {...defns.generalState} />
                    <span {...defns.stateError}>State/Province is required</span>
                </div>
            </div>
            <div {...defns.inputBoxContainer}>
                <div {...defns.inputBox}>
                    <AfitdTypography {...defns.label}>City *</AfitdTypography>
                    {/* <AftidAutocomplete {...defns.generalCity} /> */}
                    <AfitdCityAutocomplete {...defns.generalCity} />
                    <span {...defns.cityError}>City is required</span>
                </div>
                <div {...defns.inputBox}></div>
            </div>
            <AfitdEditDialog {...defns.cropModal}>
                <div {...defns.imgBeforeCropContainer}>
                    <AfitdProfilePicCropper {...defns.cropper} />
                </div>
                <div {...defns.uploadLinearProgressContainer}>
                    <AfitdLinearProgress {...defns.uploadLinearProgress} />
                </div>
            </AfitdEditDialog>
            <AfitdConfirmDialog {...defns.confirmDeletePhotoModal} />
            <AfitdConfirmDialog {...defns.cancelModal} />
            {/* <AfitdMessageDialog {...defns.badImageDialog} /> */}
        </AfitdEditDialog>
    );
}
